import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  FaBan,
  FaBriefcase,
  FaBuilding,
  FaCalendarAlt,
  FaChartLine,
  FaCheck,
  FaDownload,
  FaEnvelope,
  FaEye,
  FaFilter,
  FaIdCard,
  FaKey,
  FaMapMarkerAlt,
  FaPhone,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUserCheck,
  FaUserPlus,
  FaUserTie,
  FaUserTimes
} from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Recruiters = () => {
  const [recruiters, setRecruiters] = useState([]);
  const [filteredRecruiters, setFilteredRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterApproval, setFilterApproval] = useState('all');
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalType, setModalType] = useState('view');
  const [currentPage, setCurrentPage] = useState(1);
  const [recruitersPerPage] = useState(10);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    experience: 1,
    company: {
      name: '',
      industry: 'Technology',
      size: '11-50',
      type: 'Private Company',
      website: '',
      description: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      }
    },
    specializations: ['Technology'],
    password: ''
  });
  
  const { apiCall } = useAuth();

  useEffect(() => {
    fetchRecruiters();
  }, []);

  useEffect(() => {
    filterRecruiters();
  }, [recruiters, searchTerm, filterStatus, filterApproval]);

  const fetchRecruiters = async () => {
    try {
      setLoading(true);
      const response = await apiCall('GET', '/api/admin/recruiters');
      console.log('Recruiters API Response:', response);
      
      let recruiterArray = [];
      if (response.data?.recruiters) {
        recruiterArray = response.data.recruiters;
      } else if (Array.isArray(response.data)) {
        recruiterArray = response.data;
      } else if (response.recruiters) {
        recruiterArray = response.recruiters;
      }

      console.log('Processed recruiters:', recruiterArray);
      setRecruiters(recruiterArray);
    } catch (error) {
      console.error('Failed to fetch recruiters:', error);
      toast.error('Failed to fetch recruiters');
    } finally {
      setLoading(false);
    }
  };

  const filterRecruiters = () => {
    let filtered = recruiters;

    if (searchTerm) {
      filtered = filtered.filter(recruiter => {
        const fullName = `${recruiter.firstName || ''} ${recruiter.lastName || ''}`.toLowerCase();
        const companyName = recruiter.company?.name?.toLowerCase() || '';
        const email = recruiter.email?.toLowerCase() || '';
        const searchLower = searchTerm.toLowerCase();
        
        return fullName.includes(searchLower) || 
               email.includes(searchLower) || 
               companyName.includes(searchLower);
      });
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(recruiter => {
        switch (filterStatus) {
          case 'active':
            return recruiter.isActive === true;
          case 'inactive':
            return recruiter.isActive === false;
          default:
            return true;
        }
      });
    }

    if (filterApproval !== 'all') {
      filtered = filtered.filter(recruiter => {
        switch (filterApproval) {
          case 'approved':
            return recruiter.isApproved === true;
          case 'pending':
            return recruiter.isApproved === false;
          default:
            return true;
        }
      });
    }

    setFilteredRecruiters(filtered);
    setCurrentPage(1);
  };

  const handleCreateRecruiter = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        specializations: [formData.company.industry] // Use industry as specialization
      };
      
      console.log('Creating recruiter with payload:', payload);
      const response = await apiCall('POST', '/api/auth/register/recruiter', payload);
      
      if (response.success) {
        toast.success('Recruiter created successfully!');
        setShowCreateModal(false);
        resetFormData();
        fetchRecruiters();
      }
    } catch (error) {
      console.error('Failed to create recruiter:', error);
      toast.error(error.message || 'Failed to create recruiter');
    }
  };

  const resetFormData = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      experience: 1,
      company: {
        name: '',
        industry: 'Technology',
        size: '11-50',
        type: 'Private Company',
        website: '',
        description: '',
        address: {
          street: '',
          city: '',
          state: '',
          country: '',
          postalCode: ''
        }
      },
      specializations: ['Technology'],
      password: ''
    });
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const handleApprovalChange = async (recruiterId, approved) => {
    try {
      const response = await apiCall('PUT', `/api/admin/recruiters/${recruiterId}/approve`, { 
        approved,
        reason: approved ? 'Approved by admin' : 'Rejected by admin'
      });
      
      if (response.success || response.message) {
        fetchRecruiters();
        toast.success(`Recruiter ${approved ? 'approved' : 'rejected'} successfully`);
      }
    } catch (error) {
      console.error('Failed to update recruiter approval:', error);
      toast.error('Failed to update recruiter approval');
    }
  };

  const handleStatusChange = async (recruiterId, isActive) => {
    try {
      const response = await apiCall('PUT', `/api/admin/recruiters/${recruiterId}/status`, { 
        isActive,
        reason: isActive ? 'Activated by admin' : 'Deactivated by admin'
      });
      
      if (response.success || response.message) {
        fetchRecruiters();
        toast.success(`Recruiter ${isActive ? 'activated' : 'deactivated'} successfully`);
      }
    } catch (error) {
      console.error('Failed to update recruiter status:', error);
      toast.error('Failed to update recruiter status');
    }
  };

  const handleDeleteRecruiter = async (recruiterId) => {
    if (window.confirm('Are you sure you want to delete this recruiter? This action cannot be undone.')) {
      try {
        const response = await apiCall('DELETE', `/api/admin/recruiters/${recruiterId}`);
        if (response.success) {
          fetchRecruiters();
          toast.success('Recruiter deleted successfully');
        }
      } catch (error) {
        console.error('Failed to delete recruiter:', error);
        toast.error('Failed to delete recruiter');
      }
    }
  };

  const openModal = (type, recruiter = null) => {
    setModalType(type);
    setSelectedRecruiter(recruiter);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRecruiter(null);
    setModalType('view');
  };

  // Pagination
  const indexOfLastRecruiter = currentPage * recruitersPerPage;
  const indexOfFirstRecruiter = indexOfLastRecruiter - recruitersPerPage;
  const currentRecruiters = filteredRecruiters.slice(indexOfFirstRecruiter, indexOfLastRecruiter);
  const totalPages = Math.ceil(filteredRecruiters.length / recruitersPerPage);

  const getStatusBadge = (recruiter) => {
    if (!recruiter.isApproved) {
      return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">Pending Approval</span>;
    }
    if (!recruiter.isActive) {
      return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">Inactive</span>;
    }
    return <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">Active</span>;
  };

  const getFullName = (recruiter) => {
    return `${recruiter.firstName || ''} ${recruiter.lastName || ''}`.trim() || recruiter.email;
  };

  const getCompanyName = (recruiter) => {
    return recruiter.company?.name || 'Not specified';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Recruiters Management</h1>
          <p className="text-gray-600 mt-1">Manage recruiter accounts, approvals, and permissions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-colors"
          >
            <FaUserPlus className="mr-2" />
            Add Recruiter
          </button>
          <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-colors">
            <FaDownload className="mr-2" />
            Export Data
          </button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Recruiters</p>
              <p className="text-2xl font-bold text-gray-900">{recruiters.length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <FaUserTie className="text-white text-xl" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {recruiters.filter(r => r.isActive && r.isApproved).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-500 rounded-xl flex items-center justify-center">
              <FaUserCheck className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold text-yellow-600">
                {recruiters.filter(r => !r.isApproved).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-yellow-500 rounded-xl flex items-center justify-center">
              <FaFilter className="text-white text-xl" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive</p>
              <p className="text-2xl font-bold text-red-600">
                {recruiters.filter(r => !r.isActive).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-red-500 rounded-xl flex items-center justify-center">
              <FaUserTimes className="text-white text-xl" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="lg:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="lg:w-48">
            <select
              value={filterApproval}
              onChange={(e) => setFilterApproval(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Approvals</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Recruiters Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
      >
        {currentRecruiters.length === 0 ? (
          <div className="text-center py-12">
            <FaUserTie className="mx-auto text-4xl text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No recruiters found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <>
        <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Recruiter</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Company</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Contact</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Experience</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Joined</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
                <tbody className="divide-y divide-gray-200">
              {currentRecruiters.map((recruiter) => (
                    <tr key={recruiter._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                              {getFullName(recruiter).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                            <p className="font-semibold text-gray-900">{getFullName(recruiter)}</p>
                            <p className="text-sm text-gray-500">{recruiter.jobTitle || 'Not specified'}</p>
                      </div>
                    </div>
                  </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaBuilding className="mr-2 text-gray-400" />
                          <div>
                            <p className="font-medium">{getCompanyName(recruiter)}</p>
                            <p className="text-xs text-gray-500">{recruiter.company?.industry || 'Not specified'}</p>
                          </div>
                    </div>
                  </td>
                      <td className="px-6 py-4">
                    <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <FaEnvelope className="mr-2 text-gray-400" />
                            <span className="truncate max-w-[200px]">{recruiter.email}</span>
                      </div>
                      {recruiter.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <FaPhone className="mr-2 text-gray-400" />
                          {recruiter.phone}
                        </div>
                      )}
                    </div>
                  </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaChartLine className="mr-2 text-gray-400" />
                          {recruiter.experience || 0} years
                    </div>
                  </td>
                      <td className="px-6 py-4">{getStatusBadge(recruiter)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaCalendarAlt className="mr-2 text-gray-400" />
                    {new Date(recruiter.createdAt).toLocaleDateString()}
                        </div>
                  </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-1">
                      <button
                        onClick={() => openModal('view', recruiter)}
                            className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                          
                          {!recruiter.isApproved && (
                      <button
                              onClick={() => handleApprovalChange(recruiter._id, true)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors duration-200"
                              title="Approve Recruiter"
                      >
                              <FaCheck />
                      </button>
                          )}
                          
                      {recruiter.isActive ? (
                        <button
                              onClick={() => handleStatusChange(recruiter._id, false)}
                              className="p-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-lg transition-colors duration-200"
                              title="Deactivate Recruiter"
                        >
                          <FaBan />
                        </button>
                      ) : (
                        <button
                              onClick={() => handleStatusChange(recruiter._id, true)}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors duration-200"
                          title="Activate Recruiter"
                        >
                              <FaUserCheck />
                        </button>
                      )}
                          
                      <button
                        onClick={() => handleDeleteRecruiter(recruiter._id)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        title="Delete Recruiter"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-luxury-200">
            <div className="text-sm text-luxury-600">
              Showing {indexOfFirstRecruiter + 1} to {Math.min(indexOfLastRecruiter, filteredRecruiters.length)} of {filteredRecruiters.length} recruiters
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-luxury-300 rounded-lg hover:bg-luxury-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                    if (pageNum > totalPages) return null;
                    return (
                <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-sm border rounded-lg ${
                          currentPage === pageNum
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-luxury-300 hover:bg-luxury-50'
                  }`}
                >
                        {pageNum}
                </button>
                    );
                  })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-luxury-300 rounded-lg hover:bg-luxury-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
            )}
          </>
        )}
      </motion.div>

      {/* Create Recruiter Modal */}
      {showCreateModal && (
        <div className="luxury-modal" onClick={() => setShowCreateModal(false)}>
          <div className="luxury-modal-content max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-luxury-900">Add New Recruiter</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-luxury-400 hover:text-luxury-600 text-2xl"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleCreateRecruiter} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-luxury-900 mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">
                      <FaIdCard className="inline mr-2" />
                        First Name *
                    </label>
                    <input
                      type="text"
                      required
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="luxury-input"
                        placeholder="John"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="luxury-input"
                        placeholder="Doe"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <FaEnvelope className="inline mr-2" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="luxury-input"
                      placeholder="john@company.com"
                    />
                </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FaPhone className="inline mr-2" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="luxury-input"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                    <div className="form-group">
                      <label className="form-label">
                        <FaUserTie className="inline mr-2" />
                        Job Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.jobTitle}
                        onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
                        className="luxury-input"
                        placeholder="HR Manager"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">
                        <FaChartLine className="inline mr-2" />
                        Experience (Years)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.experience}
                        onChange={(e) => setFormData(prev => ({ ...prev, experience: parseInt(e.target.value) || 0 }))}
                        className="luxury-input"
                        placeholder="5"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div>
                  <h3 className="text-lg font-semibold text-luxury-900 mb-4">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">
                      <FaBuilding className="inline mr-2" />
                        Company Name *
                    </label>
                    <input
                      type="text"
                        required
                        value={formData.company.name}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { ...prev.company, name: e.target.value }
                        }))}
                      className="luxury-input"
                      placeholder="TechCorp Inc."
                    />
                  </div>
                    
                    <div className="form-group">
                      <label className="form-label">Industry *</label>
                      <select
                        required
                        value={formData.company.industry}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { ...prev.company, industry: e.target.value },
                          specializations: [e.target.value]
                        }))}
                        className="luxury-select"
                      >
                        <option value="Technology">Technology</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Finance">Finance</option>
                        <option value="Education">Education</option>
                        <option value="Retail">Retail</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Other">Other</option>
                      </select>
                </div>

                <div className="form-group">
                      <label className="form-label">Company Size *</label>
                      <select
                        required
                        value={formData.company.size}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { ...prev.company, size: e.target.value }
                        }))}
                        className="luxury-select"
                      >
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="501-1000">501-1000</option>
                        <option value="1000+">1000+</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Company Type</label>
                      <select
                        value={formData.company.type}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { ...prev.company, type: e.target.value }
                        }))}
                        className="luxury-select"
                      >
                        <option value="Startup">Startup</option>
                        <option value="Private Company">Private Company</option>
                        <option value="Public Company">Public Company</option>
                        <option value="Non-Profit">Non-Profit</option>
                        <option value="Government">Government</option>
                        <option value="Consulting">Consulting</option>
                        <option value="Agency">Agency</option>
                      </select>
                    </div>
                    
                    <div className="form-group md:col-span-2">
                      <label className="form-label">Website</label>
                      <input
                        type="url"
                        value={formData.company.website}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { ...prev.company, website: e.target.value }
                        }))}
                        className="luxury-input"
                        placeholder="https://www.company.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-lg font-semibold text-luxury-900 mb-4">Company Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group md:col-span-2">
                  <label className="form-label">
                        <FaMapMarkerAlt className="inline mr-2" />
                        Street Address *
                  </label>
                  <input
                    type="text"
                        required
                        value={formData.company.address.street}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { 
                            ...prev.company, 
                            address: { ...prev.company.address, street: e.target.value }
                          }
                        }))}
                    className="luxury-input"
                        placeholder="123 Main Street"
                  />
                </div>

                    <div className="form-group">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        required
                        value={formData.company.address.city}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { 
                            ...prev.company, 
                            address: { ...prev.company.address, city: e.target.value }
                          }
                        }))}
                        className="luxury-input"
                        placeholder="New York"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">State *</label>
                      <input
                        type="text"
                        required
                        value={formData.company.address.state}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { 
                            ...prev.company, 
                            address: { ...prev.company.address, state: e.target.value }
                          }
                        }))}
                        className="luxury-input"
                        placeholder="NY"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Country *</label>
                      <input
                        type="text"
                        required
                        value={formData.company.address.country}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { 
                            ...prev.company, 
                            address: { ...prev.company.address, country: e.target.value }
                          }
                        }))}
                        className="luxury-input"
                        placeholder="USA"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Postal Code</label>
                      <input
                        type="text"
                        value={formData.company.address.postalCode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          company: { 
                            ...prev.company, 
                            address: { ...prev.company.address, postalCode: e.target.value }
                          }
                        }))}
                        className="luxury-input"
                        placeholder="10001"
                      />
                    </div>
                  </div>
                </div>

                {/* Account */}
                <div>
                  <h3 className="text-lg font-semibold text-luxury-900 mb-4">Account Settings</h3>
                <div className="form-group">
                  <label className="form-label">
                    <FaKey className="inline mr-2" />
                    Password *
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="luxury-input flex-1"
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={generatePassword}
                      className="luxury-button-secondary text-sm"
                    >
                      Generate
                    </button>
                  </div>
                </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-luxury-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="luxury-button-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="luxury-button-primary"
                  >
                    <FaUserPlus className="mr-2" />
                    Create Recruiter
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showModal && selectedRecruiter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Recruiter Details</h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-8">
                {/* Header with Avatar and Basic Info */}
                <div className="flex items-center space-x-6 p-6 bg-gray-50 rounded-xl">
                  <div className="h-20 w-20 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">
                      {getFullName(selectedRecruiter).charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold text-gray-900">{getFullName(selectedRecruiter)}</h3>
                    <p className="text-gray-600 text-lg">{selectedRecruiter.jobTitle || 'Position not specified'}</p>
                    <p className="text-gray-500">{selectedRecruiter.email}</p>
                    <div className="flex space-x-3 mt-3">
                      {getStatusBadge(selectedRecruiter)}
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {selectedRecruiter.experience || 0} years experience
                      </span>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <FaIdCard className="mr-3 text-blue-500" />
                    Personal Information
                  </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                  <div>
                        <label className="text-sm font-medium text-gray-500">First Name</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.firstName || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Last Name</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.lastName || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email Address</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.email}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone Number</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Job Title</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.jobTitle || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Experience</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.experience || 0} years</p>
                      </div>
                    </div>
                    </div>
                  </div>

                {/* Company Information */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <FaBuilding className="mr-3 text-green-500" />
                    Company Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                  <div>
                        <label className="text-sm font-medium text-gray-500">Company Name</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.company?.name || 'Not provided'}</p>
                    </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Industry</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.company?.industry || 'Not provided'}</p>
                  </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company Size</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.company?.size || 'Not provided'}</p>
                </div>
              </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company Type</label>
                        <p className="text-gray-900 font-medium">{selectedRecruiter.company?.type || 'Not provided'}</p>
            </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Website</label>
                        {selectedRecruiter.company?.website ? (
                          <a 
                            href={selectedRecruiter.company.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium underline"
                          >
                            {selectedRecruiter.company.website}
                          </a>
                        ) : (
                          <p className="text-gray-900 font-medium">Not provided</p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Registration Date</label>
                        <p className="text-gray-900 font-medium">
                          {new Date(selectedRecruiter.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {selectedRecruiter.company?.description && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-500">Company Description</label>
                      <p className="text-gray-900 font-medium mt-1 p-3 bg-gray-50 rounded-lg">
                        {selectedRecruiter.company.description}
                      </p>
                    </div>
                  )}
                </div>

                {/* Company Address */}
                {selectedRecruiter.company?.address && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <FaMapMarkerAlt className="mr-3 text-red-500" />
                      Company Address
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Street Address</label>
                          <p className="text-gray-900 font-medium">{selectedRecruiter.company.address.street || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">City</label>
                          <p className="text-gray-900 font-medium">{selectedRecruiter.company.address.city || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">State/Province</label>
                          <p className="text-gray-900 font-medium">{selectedRecruiter.company.address.state || 'Not provided'}</p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-500">Country</label>
                          <p className="text-gray-900 font-medium">{selectedRecruiter.company.address.country || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Postal Code</label>
                          <p className="text-gray-900 font-medium">{selectedRecruiter.company.address.postalCode || 'Not provided'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-500">Full Address</label>
                          <p className="text-gray-900 font-medium p-3 bg-gray-50 rounded-lg">
                            {[
                              selectedRecruiter.company.address.street,
                              selectedRecruiter.company.address.city,
                              selectedRecruiter.company.address.state,
                              selectedRecruiter.company.address.country,
                              selectedRecruiter.company.address.postalCode
                            ].filter(Boolean).join(', ') || 'Complete address not provided'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Specializations */}
                {selectedRecruiter.specializations && selectedRecruiter.specializations.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <FaBriefcase className="mr-3 text-purple-500" />
                      Specializations
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecruiter.specializations.map((spec, index) => (
                        <span 
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Account Status */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <FaUserCheck className="mr-3 text-orange-500" />
                    Account Status
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Approval Status</label>
                      <p className="text-gray-900 font-medium">
                        {selectedRecruiter.isApproved ? (
                          <span className="text-green-600 flex items-center">
                            <FaCheck className="mr-1" /> Approved
                          </span>
                        ) : (
                          <span className="text-yellow-600 flex items-center">
                            <FaFilter className="mr-1" /> Pending Approval
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Account Status</label>
                      <p className="text-gray-900 font-medium">
                        {selectedRecruiter.isActive ? (
                          <span className="text-green-600 flex items-center">
                            <FaUserCheck className="mr-1" /> Active
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center">
                            <FaBan className="mr-1" /> Inactive
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Updated</label>
                      <p className="text-gray-900 font-medium">
                        {new Date(selectedRecruiter.updatedAt || selectedRecruiter.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  {!selectedRecruiter.isApproved && (
                    <button
                      onClick={() => {
                        handleApprovalChange(selectedRecruiter._id, true);
                        closeModal();
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium flex items-center transition-colors"
                    >
                      <FaCheck className="mr-2" />
                      Approve Recruiter
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      handleStatusChange(selectedRecruiter._id, !selectedRecruiter.isActive);
                      closeModal();
                    }}
                    className={`px-6 py-2 rounded-lg font-medium flex items-center transition-colors ${
                      selectedRecruiter.isActive 
                        ? "bg-yellow-600 hover:bg-yellow-700 text-white" 
                        : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                  >
                    {selectedRecruiter.isActive ? (
                      <>
                        <FaBan className="mr-2" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <FaUserCheck className="mr-2" />
                        Activate
                      </>
                    )}
                  </button>

                  <button
                    onClick={closeModal}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recruiters; 